import { FormTemplate, FormTemplateSection, SectionType } from 'data/template/template.types';
import isNil from 'lodash.isnil';
import omitBy from 'lodash.omitby';
import {
  AccessFunctionFormData,
  ACCESS_FUNCTION_INTERFACE_TEMPORARY_ID,
  ContentInterfaceFormData,
  ProvisioningInterfaceFormData,
  DataInterfaceFormData,
  AccessFunctionInterfaceFormSectionType,
  AccessFunctionInterfaceFormData,
  AccessFunction,
} from 'data/accessFunction/accessFunction.types';

import { TabProp } from 'shared/multi-tab/MultiTab';
import { getInterfaceFormTemplateSectionName } from './accessFunctionTemplate.adapter';

let nextId = 1;

// eslint-disable-next-line import/prefer-default-export
export function createAccessFunctionBasedOnTemplate(
  template?: FormTemplate,
  forceInterfaceCreation = false,
  formSectionName?: AccessFunctionInterfaceFormSectionType,
  tabs: TabProp[] = []
): Partial<AccessFunctionFormData> {
  if (template == null) {
    return {};
  }

  const accessFunctionForm: Partial<AccessFunctionFormData> = {
    ...getFieldValuesFromSection(template.general as FormTemplateSection),
    ...getFieldValuesFromSection(template.systemInformation as FormTemplateSection),
    ...getFieldValuesFromSubsection(template),
    dataInterfaces: getInterfaceFieldValuesFromSection(
      template,
      'dataInterfaces',
      forceInterfaceCreation,
    ),
    provisioningInterfaces: getInterfaceFieldValuesFromSection(
      template,
      'provisioningInterfaces',
      forceInterfaceCreation,
    ),
    contentInterfaces: getInterfaceFieldValuesFromSection(
      template,
      'contentInterfaces',
      forceInterfaceCreation,
    ),
    afOptions: getInterfaceFieldValuesFromSection(
      template,
      'afOptions',
      forceInterfaceCreation,
    ),
    monitoringCenters: getInterfaceFieldValuesFromSection(
      template,
      'monitoringCenters',
      forceInterfaceCreation,
    ),
    territories: getInterfaceFieldValuesFromSection(
      template,
      'territories',
      forceInterfaceCreation,
    ),
    eimsNetworkElement: getInterfaceFieldValuesFromSection(
      template,
      'eimsNetworkElement',
      forceInterfaceCreation,
    ),
    diameter: getInterfaceFieldValuesFromSection(
      template,
      'diameter',
      forceInterfaceCreation,
    ),
    usnbdAgencies: getInterfaceFieldValuesFromSection(
      template,
      'usnbdAgencies',
      forceInterfaceCreation,
    ),
    usnbdCallDataChannels: getInterfaceFieldValuesFromSection(
      template,
      'usnbdCallDataChannels',
      forceInterfaceCreation,
    ),
  };
  const formData = omitBy(accessFunctionForm, isNil) as unknown as AccessFunctionFormData;

  if (template && template.key === 'PCRFIPQ' && formSectionName != null) {
    (formData[formSectionName] as AccessFunctionInterfaceFormData[])[0].rolepriority = 'SECONDARY';
  }

  if (
    template &&
    [
      'ERICSSON_LIMS',
      'ERICSSON_LIMS_CORBA',
      'VIETTEL_MD',
      'SIEMENS_LIMS',
      'COGNYTE',
      'VRNT_SG'
    ].includes(template.key) &&
    formSectionName != null &&
    tabs?.length > 0
  ) {
    if (tabs[0].panel.props.defaults.deliveryFormat === 'TS201671') {
      (formData[
        formSectionName
      ] as AccessFunctionInterfaceFormData[])[0].deliveryFormat = 'TS33108';
    } else {
      (formData[
        formSectionName
      ] as AccessFunctionInterfaceFormData[])[0].deliveryFormat = 'TS201671';
    }
  }

  return formData;
}

export interface createAFOptions {
  template?: FormTemplate;
  forceInterfaceCreation?: boolean;
  formSectionName?: AccessFunctionInterfaceFormSectionType;
  tabs?: TabProp[];
  accessFunctions?: AccessFunction[];
}

function getFieldValuesFromSection(
  templateSection: FormTemplateSection,
): Partial<AccessFunctionFormData> {
  if (!templateSection) {
    return {};
  }

  return Object.entries(templateSection?.fields)
    .filter(([_fieldName, field]) => field.initial != null)
    .reduce((previousValue, [fieldName, field]) => ({
      ...previousValue,
      [fieldName]: field.initial,
    }), {});
}

function getFieldValuesFromSubsection(
  template: FormTemplate,
): Partial<AccessFunctionFormData> {
  if (template.key === 'ETSI103221') {
    return {
      contentDeliveryVia: 'ss8Distributor',
      dataDeliveryVia: 'ss8Distributor',
    };
  }
  return {};
}

function getInterfaceFieldValuesFromSection(
  template: FormTemplate,
  interfaceName: AccessFunctionInterfaceFormSectionType,
  forceInterfaceCreation = false,
): (ContentInterfaceFormData | ProvisioningInterfaceFormData | DataInterfaceFormData)[] | undefined {
  const generalSection = getFieldValuesFromSection(template.general as FormTemplateSection);
  const contentDeliveryViaTypeInGeneral = generalSection?.contentDeliveryVia ?? '';

  let templateSectionName = getInterfaceFormTemplateSectionName(template, interfaceName, contentDeliveryViaTypeInGeneral);

  // for PNF2CNF project
  const templateKey = template.key;
  if (templateKey === 'ALU_MME' ||
    templateKey === 'ERK_VMME' ||
    templateKey === 'NSN_SAEGW' ||
    templateKey === 'ALU_SGW' ||
    templateKey === 'NSN_SAEGW_PGW' ||
    templateKey === 'STA_PGW' ||
    templateKey === 'ERK_SGW' ||
    templateKey === 'ERK_PGW' ||
    templateKey === 'ACME_X123' ||
    templateKey === 'NOKIA_SBC') {

    const contentInterfacesSection = getFieldValuesFromSection(template.contentInterfaces as FormTemplateSection);
    let contentDeliveryViaTypeInInterface = contentInterfacesSection?.contentDeliveryVia ?? contentDeliveryViaTypeInGeneral;
    if (contentDeliveryViaTypeInGeneral === 'trafficManager') {
      contentDeliveryViaTypeInInterface = 'trafficManager';
    }
    const dataInterfacesSection = getFieldValuesFromSection(template.dataInterfaces as FormTemplateSection);
    const dataDeliveryViaTypeInInterface = dataInterfacesSection?.dataDeliveryVia ?? 'directFromAF';

    templateSectionName = getInterfaceFormTemplateSectionName(template, interfaceName, contentDeliveryViaTypeInInterface);
    if (interfaceName === 'dataInterfaces') {
      templateSectionName = getInterfaceFormTemplateSectionName(template, interfaceName, dataDeliveryViaTypeInInterface);
    }
  }

  const templateSection = template[templateSectionName] as FormTemplateSection;

  if (templateSection == null) {
    return;
  }

  const expandByDefault = (template[templateSectionName] as FormTemplateSection).metadata.expandByDefault || false;
  if (!forceInterfaceCreation && (
    ((template[templateSectionName] as FormTemplateSection).metadata.min === 0 && expandByDefault !== true) ||
    templateSection?.metadata?.sectionType === SectionType.distributorConfiguration
  )) {
    return [];
  }

  const fields = {
    ...getFieldValuesFromSection(template[templateSectionName] as FormTemplateSection),
    id: `${nextId}.${ACCESS_FUNCTION_INTERFACE_TEMPORARY_ID}`,
  };

  nextId += 1;

  return [fields as ContentInterfaceFormData | ProvisioningInterfaceFormData | DataInterfaceFormData];
}
